import React, { useEffect, useState } from "react";
import API from "../api";
import { Card, Badge, Toast, Loading, Empty, SectionHeader } from "../components/UI";
import { useAuth } from "../context/AuthContext";

const STATUS_OPTIONS = ["New", "Contacted", "Interested", "Closed", "Lost"];
const STATUS_COLORS = {
  New: "blue", Contacted: "orange", Interested: "green", Closed: "green", Lost: "red"
};

const BACKEND = "https://dealerbot-backend-skr6.onrender.com";

export default function Leads() {
  const { token, user } = useAuth();

  // ── Leads state ──
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [filter, setFilter] = useState({ clientId: "", interest: "", score: "", status: "" });
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [editLead, setEditLead] = useState(null);
  const [editForm, setEditForm] = useState({ status: "", notes: "", followUpDate: "" });

  // ── Conversations state ──
  const [tab, setTab] = useState("leads"); // "leads" | "conversations"
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(false);
  const [convError, setConvError] = useState(null);
  const [selectedConv, setSelectedConv] = useState(null);

  // ── Load leads + clients ──
  useEffect(() => {
    Promise.all([API.get("/leads"), API.get("/clients")])
      .then(([l, c]) => { setLeads(l.data); setClients(c.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // ── Load conversations when tab switches ──
  useEffect(() => {
    if (tab !== "conversations") return;
    if (conversations.length > 0) return; // already loaded
    fetchConversations();
  }, [tab]);

  const fetchConversations = async () => {
    setConvLoading(true);
    setConvError(null);
    try {
      const clientId = user?.clientId;
      if (!clientId) throw new Error("clientId not found");
      const res = await fetch(
        `${BACKEND}/api/conversations?clientId=${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || "Failed");
      setConversations(json.conversations || []);
    } catch (err) {
      setConvError(err.message);
    } finally {
      setConvLoading(false);
    }
  };

  // ── Leads helpers ──
  const deleteLead = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await API.delete(`/lead/${id}`);
      setLeads(p => p.filter(l => l._id !== id));
      setToast({ message: "Lead deleted", type: "success" });
    } catch { setToast({ message: "Failed", type: "error" }); }
  };

  const openEdit = (lead) => {
    setEditLead(lead);
    setEditForm({
      status: lead.status || "New",
      notes: lead.notes || "",
      followUpDate: lead.followUpDate ? lead.followUpDate.split("T")[0] : "",
    });
  };

  const saveEdit = async () => {
    try {
      await API.put(`/lead/${editLead._id}`, editForm);
      setLeads(p => p.map(l => l._id === editLead._id ? { ...l, ...editForm } : l));
      setEditLead(null);
      setToast({ message: "Lead updated!", type: "success" });
    } catch { setToast({ message: "Failed", type: "error" }); }
  };

  const filtered = leads.filter(l => {
    if (filter.clientId && l.clientId?._id !== filter.clientId) return false;
    if (filter.interest && l.interest !== filter.interest) return false;
    if (filter.score && l.score !== filter.score) return false;
    if (filter.status && l.status !== filter.status) return false;
    return true;
  });

  // ── Styles ──
  const sel = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "8px 12px", color: "var(--text-primary)", fontSize: 13, fontFamily: "var(--font-body)", cursor: "pointer", outline: "none" };
  const th = { textAlign: "left", padding: "12px 16px", fontSize: 11, color: "var(--text-muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", background: "var(--bg-elevated)" };
  const td = { padding: "11px 16px", fontSize: 13, verticalAlign: "middle" };

  if (loading) return <Loading />;

  return (
    <div className="fade-in">
      <SectionHeader
        title="Leads & CRM"
        subtitle={tab === "leads" ? `${filtered.length} leads` : `${conversations.length} conversations`}
      />

      {/* ── Tabs ── */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20, borderBottom: "1px solid var(--border)", paddingBottom: 0 }}>
        {[
          { key: "leads", label: "🎯 Leads" },
          { key: "conversations", label: "💬 Conversations" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              background: "transparent",
              border: "none",
              borderBottom: tab === t.key ? "2px solid var(--accent)" : "2px solid transparent",
              color: tab === t.key ? "var(--accent)" : "var(--text-secondary)",
              padding: "10px 18px",
              fontSize: 13,
              fontWeight: tab === t.key ? 700 : 500,
              cursor: "pointer",
              marginBottom: -1,
              transition: "all 0.15s",
              fontFamily: "var(--font-body)",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════ LEADS TAB ══════════════ */}
      {tab === "leads" && (
        <>
          {/* Filters */}
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <select value={filter.clientId} onChange={e => setFilter(p => ({ ...p, clientId: e.target.value }))} style={sel}>
              <option value="">All Clients</option>
              {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
            </select>
            <select value={filter.score} onChange={e => setFilter(p => ({ ...p, score: e.target.value }))} style={sel}>
              <option value="">All Scores</option>
              <option value="hot">🔥 Hot</option>
              <option value="warm">🌡 Warm</option>
              <option value="cold">❄️ Cold</option>
            </select>
            <select value={filter.status} onChange={e => setFilter(p => ({ ...p, status: e.target.value }))} style={sel}>
              <option value="">All Status</option>
              {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            {Object.values(filter).some(Boolean) && (
              <button onClick={() => setFilter({ clientId: "", interest: "", score: "", status: "" })}
                style={{ background: "transparent", border: "1px solid rgba(255,77,77,0.3)", borderRadius: "var(--radius)", color: "var(--accent-red)", padding: "8px 12px", fontSize: 12, cursor: "pointer" }}>
                ✕ Clear
              </button>
            )}
          </div>

          {filtered.length === 0 ? <Card><Empty message="No leads found." icon="◎" /></Card> : (
            <Card style={{ padding: 0, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    {["Phone", "Name", "Interest", "Score", "Status", "Follow Up", "Client", "Actions"].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((l, i) => (
                    <tr key={l._id} style={{ borderBottom: "1px solid var(--border)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                      <td style={{ ...td, fontFamily: "var(--font-mono)", fontSize: 12 }}>{l.phone}</td>
                      <td style={{ ...td, color: l.name ? "var(--text-primary)" : "var(--text-muted)" }}>{l.name || "—"}</td>
                      <td style={td}>
                        <Badge color={l.interest === "Bike Details" ? "orange" : l.interest === "Service Booking" ? "blue" : "grey"}>
                          {l.interest}
                        </Badge>
                      </td>
                      <td style={td}>
                        <Badge color={l.score === "hot" ? "red" : l.score === "warm" ? "orange" : "grey"}>{l.score}</Badge>
                      </td>
                      <td style={td}>
                        <Badge color={STATUS_COLORS[l.status] || "grey"}>{l.status || "New"}</Badge>
                      </td>
                      <td style={{ ...td, fontSize: 11, color: l.followUpDate ? "var(--accent-orange)" : "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
                        {l.followUpDate ? new Date(l.followUpDate).toLocaleDateString("en-IN") : "—"}
                      </td>
                      <td style={{ ...td, color: "var(--text-secondary)" }}>{l.clientId?.name || "—"}</td>
                      <td style={td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => openEdit(l)} title="Edit"
                            style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 12, color: "var(--accent)" }}>
                            ✎
                          </button>
                          <button onClick={() => deleteLead(l._id)} title="Delete"
                            style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 6, width: 28, height: 28, cursor: "pointer", fontSize: 12, color: "var(--accent-red)" }}>
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      )}

      {/* ══════════════ CONVERSATIONS TAB ══════════════ */}
      {tab === "conversations" && (
        <>
          {convLoading && <Loading />}
          {convError && (
            <Card>
              <div style={{ color: "var(--accent-red)", fontSize: 13 }}>⚠️ {convError}</div>
            </Card>
          )}
          {!convLoading && !convError && conversations.length === 0 && (
            <Card><Empty message="No conversations yet." icon="💬" /></Card>
          )}
          {!convLoading && !convError && conversations.length > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "300px 1fr", gap: 16, alignItems: "start" }}>

              {/* ── Conversation List ── */}
              <Card style={{ padding: 0, overflow: "hidden" }}>
                {conversations.map((c, i) => {
                  const lastMsg = c.messages?.[c.messages.length - 1];
                  const isSelected = selectedConv?.id === c.id;
                  return (
                    <div
                      key={c.id}
                      onClick={() => setSelectedConv(c)}
                      style={{
                        padding: "12px 16px",
                        borderBottom: i < conversations.length - 1 ? "1px solid var(--border)" : "none",
                        cursor: "pointer",
                        background: isSelected ? "rgba(255,255,255,0.05)" : "transparent",
                        borderLeft: isSelected ? "3px solid var(--accent)" : "3px solid transparent",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-primary)", fontWeight: 600 }}>
                          {c.phone}
                        </span>
                        <Badge color={c.step === "greeting" ? "grey" : "green"} style={{ fontSize: 10 }}>
                          {c.step}
                        </Badge>
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {lastMsg?.text || lastMsg?.content || "No messages"}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>
                        {new Date(c.updated_at).toLocaleString("en-IN")}
                      </div>
                    </div>
                  );
                })}
              </Card>

              {/* ── Message Thread ── */}
              <Card style={{ minHeight: 400 }}>
                {!selectedConv ? (
                  <Empty message="Select a conversation to view messages." icon="👈" />
                ) : (
                  <>
                    <div style={{ marginBottom: 16, paddingBottom: 12, borderBottom: "1px solid var(--border)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{selectedConv.phone}</div>
                        <div style={{ fontSize: 11, color: "var(--text-muted)" }}>
                          {selectedConv.messages?.length || 0} messages · Step: {selectedConv.step}
                        </div>
                      </div>
                      <Badge color="blue">{selectedConv.data?.interest || "—"}</Badge>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 500, overflowY: "auto" }}>
                      {(!selectedConv.messages || selectedConv.messages.length === 0) ? (
                        <Empty message="No messages in this session." icon="📭" />
                      ) : (
                        selectedConv.messages.map((msg, i) => {
                          const isUser = msg.role === "user" || msg.from === "user";
                          return (
                            <div key={i} style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
                              <div style={{
                                maxWidth: "70%",
                                padding: "8px 12px",
                                borderRadius: isUser ? "12px 12px 2px 12px" : "12px 12px 12px 2px",
                                background: isUser ? "var(--accent)" : "var(--bg-elevated)",
                                color: isUser ? "#000" : "var(--text-primary)",
                                fontSize: 13,
                                lineHeight: 1.5,
                              }}>
                                {msg.text || msg.content || msg.message || JSON.stringify(msg)}
                                <div style={{ fontSize: 10, opacity: 0.6, marginTop: 4, textAlign: isUser ? "right" : "left" }}>
                                  {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString("en-IN") : ""}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </>
                )}
              </Card>
            </div>
          )}
        </>
      )}

      {/* ── Edit Lead Modal ── */}
      {editLead && (
        <div style={modalOverlay}>
          <div style={modalBox}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>
              Edit Lead — {editLead.phone}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={labelS}>Status</label>
                <select value={editForm.status} onChange={e => setEditForm(p => ({ ...p, status: e.target.value }))} style={{ ...sel, width: "100%" }}>
                  {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={labelS}>Follow Up Date</label>
                <input type="date" value={editForm.followUpDate}
                  onChange={e => setEditForm(p => ({ ...p, followUpDate: e.target.value }))}
                  style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "10px 14px", color: "var(--text-primary)", fontSize: 14, outline: "none", width: "100%" }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <label style={labelS}>Notes</label>
                <textarea value={editForm.notes}
                  onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Koi notes daalo..."
                  rows={3}
                  style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: "var(--radius)", padding: "10px 14px", color: "var(--text-primary)", fontSize: 14, outline: "none", resize: "vertical", fontFamily: "var(--font-body)" }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 16 }}>
              <button onClick={() => setEditLead(null)}
                style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius)", color: "var(--text-secondary)", padding: "9px 18px", fontSize: 13, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={saveEdit}
                style={{ background: "var(--accent)", border: "none", borderRadius: "var(--radius)", color: "#000", padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const modalOverlay = { position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.6)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" };
const modalBox = { background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: "var(--radius-lg)", padding: 28, width: "100%", maxWidth: 420, boxShadow: "var(--shadow)" };
const labelS = { fontSize: 12, color: "var(--text-secondary)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em" };